"use client";

import { useCallback, useRef, useState } from "react";
import type { CalendarRevealRequest } from "../components/calendar/revealCalendarTile";
import type { PublicationModel } from "../types/models";

/** A frozen viewport box. Plain numbers, not the DOMRect they came from: this
 *  outlives the element it was measured off — a period change unmounts that
 *  tile — and a live DOMRect would invite someone to re-read it. */
export type PinnedAnchorRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

/**
 * The anchor tile's box, or null when there is nothing worth pinning to. A
 * DETACHED node measures as an all-zero DOMRect, which is truthy — pinning to
 * it would park the panel in the viewport's top-left corner — so refuse, and
 * let the popover's own connected-anchor gate close it instead.
 */
const measurePinnedRect = (
  element: HTMLElement | null,
): PinnedAnchorRect | null => {
  if (!element?.isConnected) return null;
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;
  return {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
  };
};

export type CalendarPostPopoverState = {
  /**
   * Snapshot of the currently inspected post, updated on each navigation.
   * The live publications list supplies its latest data when available.
   */
  publication: PublicationModel;
  /**
   * The tile it was opened from (null in a sheet). Still the panel's open gate
   * and still the click-outside exemption — but it stops being what the panel
   * is POSITIONED against the moment `pinnedRect` is set.
   */
  element: HTMLElement | null;
  /**
   * The post the panel is SHOWING. Moves on every step, and it — not
   * the origin — is what the calendar tints: the panel no longer walks to the
   * destination's tile, so this marker is the only thing left that says which post
   * is open.
   */
  inspectedPublicationId: string;
  /**
   * Where the panel is nailed down, in viewport coordinates — the anchor
   * tile's rect at the moment of the FIRST step, and null until then.
   * Set once and never re-measured, because the grid then has to be free to
   * scroll, change month and unmount that tile with the panel bolted to the
   * screen. See CalendarPostPopover for why a rect, and not the tile, is what
   * holds it still. Cleared by `open()`.
   */
  pinnedRect: PinnedAnchorRect | null;
};

/**
 * The global calendar's post popover: which post, shown where — and, once the
 * user pages, no longer anchored to a tile at all. The player resets when
 * navigating to a different video; the controls stay mounted. Closing hands
 * focus back to the tile of the post that was on screen.
 */
export const useCalendarPostPopover = () => {
  const [state, setState] = useState<CalendarPostPopoverState | null>(null);
  /**
   * The reveal is kept BESIDE the state, not inside it: it is the calendar's
   * business, not the panel's. Its token comes from a ref that never resets —
   * Calendar latches on the number to handle each request exactly once, and a
   * counter that restarted at 1 on every open would make the second
   * inspector's first step a silent no-op.
   */
  const [reveal, setReveal] = useState<CalendarRevealRequest | null>(null);
  const stateRef = useRef<CalendarPostPopoverState | null>(null);
  const revealTokenRef = useRef(0);

  // The mirror is written SYNCHRONOUSLY here rather than in an effect, because
  // two steps can land in one tick (a held ‹, a double-click on ›) and the
  // second must see the first — above all the rect the first froze, or it
  // would re-measure against a panel that has already stopped moving.
  const commit = useCallback((next: CalendarPostPopoverState | null) => {
    stateRef.current = next;
    setState(next);
  }, []);

  /**
   * A tile click, or a deep link — a full reset. The pin comes off (which is
   * what walks the panel back to a live anchor that tracks its tile) and the
   * inspected post becomes the clicked one, which is also what makes
   * re-clicking the anchor tile after paging land back on that post instead of
   * being a dead click. No reveal: the tile the user just clicked is already
   * under their cursor.
   */
  const open = useCallback(
    (publication: PublicationModel, element: HTMLElement | null) => {
      commit({
        publication,
        element,
        inspectedPublicationId: publication.id,
        pinnedRect: null,
      });
      setReveal(null);
    },
    [commit],
  );

  /**
   * Scroll the calendar to a tile and pulse it — no inspector. Used by List →
   * "Show in calendar": the grid's `revealRequest` path already does the
   * period change, the scroll and the bump; this only issues the request.
   * Also the body of `inspect`'s reveal branch, so both share one token.
   */
  const revealPublication = useCallback((publicationId: string) => {
    revealTokenRef.current += 1;
    setReveal({
      publicationId,
      token: revealTokenRef.current,
    });
  }, []);

  /**
   * The inspector moved to another calendar post. Update its video and post
   * snapshot while retaining the original anchor: what it shows moves,
   * where it sits does not.
   *
   * The rect is frozen here, inside the pager's own click handler, before React
   * has rendered anything and before Calendar's reveal has scrolled a pixel;
   * measuring it later would freeze the panel at a position it was never at.
   *
   * `options.reveal` is false on the narrow sheet, which covers the calendar
   * completely — scrolling it there would move something the user cannot see
   * and land them somewhere else on dismissal.
   */
  const inspect = useCallback(
    (publication: PublicationModel, options: { reveal: boolean }) => {
      const previous = stateRef.current;
      if (!previous) return;
      commit({
        ...previous,
        publication,
        inspectedPublicationId: publication.id,
        pinnedRect: previous.pinnedRect ?? measurePinnedRect(previous.element),
      });
      if (!options.reveal) return;
      revealPublication(publication.id);
    },
    [commit, revealPublication],
  );

  const close = useCallback(() => {
    const previous = stateRef.current;
    commit(null);
    setReveal(null);
    if (!previous) return;
    // Focus goes back to the tile of the post that was ON SCREEN, not the one
    // the inspector was opened from: after paging they are different tiles, and
    // after a cross-period step the origin is not in the document at all, so
    // focusing it would silently drop focus to the body.
    const inspectedTile = document.querySelector<HTMLElement>(
      `[data-publication-id="${previous.inspectedPublicationId}"]`,
    );
    const target =
      inspectedTile ?? (previous.element?.isConnected ? previous.element : null);
    target?.focus?.({ preventScroll: true });
  }, [commit]);

  return {
    state, reveal, open, inspect, revealPublication, close,
  };
};
